import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineShieldCheck,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService } from "../../services";
import masterDataService from "../../services/masterDataService";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";
import "./PPEList.css";

const PPEList = () => {
  const [ppeList, setPPEList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingPPE, setEditingPPE] = useState(null);
  const [formData, setFormData] = useState({
    ppeCode: "",
    ppeName: "",
    category: "",
    description: "",
    status: "Active",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadPPEList();
    } else {
      setError("Please login to view PPE list");
      setLoading(false);
    }
  }, [tenantId]);

  const loadPPEList = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await masterDataService.getPPEList(tenantId);
      setPPEList(data);
    } catch (err) {
      setError("Failed to load PPE list. Please try again.");
      console.error("Error loading PPE list:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredPPEList = ppeList.filter(
    (ppe) =>
      ppe.ppeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ppe.ppeCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ppe.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ppe.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingPPE(null);
    setFormData({
      ppeCode: "",
      ppeName: "",
      category: "",
      description: "",
      status: "Active",
    });
    setFormErrors({});
    setShowDrawer(true);
  };

  const handleEdit = (ppe) => {
    setEditingPPE(ppe);
    setFormData({
      ppeCode: ppe.ppeCode || "",
      ppeName: ppe.ppeName || "",
      category: ppe.category || "",
      description: ppe.description || "",
      status: ppe.status || "Active",
    });
    setFormErrors({});
    setShowDrawer(true);
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      await masterDataService.deletePPE(id);
      await loadPPEList();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete PPE. Please try again.");
      console.error("Error deleting PPE:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    const codeRegex = /^[A-Z0-9_-]+$/;
    const nameRegex = /^[A-Za-z0-9\s().,/-]+$/;

    // PPE Code validations
    if (!formData.ppeCode.trim()) {
      errors.ppeCode = "PPE code is required";
    } else if (formData.ppeCode.length > 50) {
      errors.ppeCode = "PPE code must be less than 50 characters";
    } else if (!codeRegex.test(formData.ppeCode.trim())) {
      errors.ppeCode = "Code can only contain letters, numbers, underscores, and hyphens";
    }

    // PPE Name validations
    if (!formData.ppeName.trim()) {
      errors.ppeName = "PPE name is required";
    } else if (formData.ppeName.length > 100) {
      errors.ppeName = "PPE name must be less than 100 characters";
    } else if (!nameRegex.test(formData.ppeName.trim())) {
      errors.ppeName = "Name contains invalid characters";
    } else {
      // Duplicate name check
      const isDuplicateName = ppeList.some(
        (ppe) =>
          ppe.ppeName?.toLowerCase() === formData.ppeName.trim().toLowerCase() &&
          ppe.ppeId !== editingPPE?.ppeId,
      );
      if (isDuplicateName) {
        errors.ppeName = "PPE name already exists";
      }
    }

    if (formData.category && formData.category.length > 100) {
      errors.category = "Category must be less than 100 characters";
    }

    if (formData.description && formData.description.length > 255) {
      errors.description = "Description must be less than 255 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const ppeData = {
        tenantId: tenantId,
        ppeCode: formData.ppeCode.trim(),
        ppeName: formData.ppeName.trim(),
        category: formData.category.trim() || null,
        description: formData.description.trim() || null,
        status: formData.status,
      };

      if (editingPPE) {
        ppeData.ppeId = editingPPE.ppeId;
        await masterDataService.updatePPE(editingPPE.ppeId, ppeData);
      } else {
        await masterDataService.createPPE(ppeData);
      }

      await loadPPEList();
      setShowDrawer(false);
      setFormData({
        ppeCode: "",
        ppeName: "",
        category: "",
        description: "",
        status: "Active",
      });
      setEditingPPE(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save PPE. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingPPE(null);
    setFormData({
      ppeCode: "",
      ppeName: "",
      category: "",
      description: "",
      status: "Active",
    });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading PPE list..." />;
  }

  return (
    <div className="ppe-list-page">
      <div className="page-header">
        <div className="header-content">
          <h1>PPE List</h1>
          <p>
            Manage Personal Protective Equipment (PPE) items for safety
            compliance
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add PPE
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container kizantable">
        <div className="table-header">
          <div className="left-group">
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search PPE items..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
            <div className="table-info">
              {filteredPPEList.length}{" "}
              {filteredPPEList.length === 1 ? "PPE item" : "PPE items"}
            </div>
          </div>
        </div>

        {filteredPPEList.length === 0 ? (
          <div className="empty-state">
            <HiOutlineShieldCheck className="empty-icon" />
            <h3>No PPE items found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first PPE item"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                <HiOutlinePlus className="btn-icon" />
                New PPE
              </button>
            )}
          </div>
        ) : (
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPPEList.map((ppe) => (
                  <tr key={ppe.ppeId}>
                    <td className="code-cell">{ppe.ppeCode}</td>
                    <td className="name-cell">{ppe.ppeName}</td>
                    <td>
                      {ppe.category ? (
                        <span className="category-pill">{ppe.category}</span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="description-cell">
                      {ppe.description || "-"}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${ppe.status?.toLowerCase()}`}
                      >
                        {ppe.status || "Active"}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(ppe)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(ppe)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showDrawer}
        onClose={handleCloseDrawer}
        title={editingPPE ? "Edit PPE" : "New PPE"}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseDrawer}
            >
              Cancel
            </button>
            <button type="submit" form="ppe-form" className="btn-primary" disabled={submitting}>
              {submitting
                ? "Saving..."
                : editingPPE
                  ? "Update PPE"
                  : "Save PPE"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="ppe-form" className="modal-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="ppeCode">
                  PPE Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="ppeCode"
                  value={formData.ppeCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      ppeCode: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9_-]/g, ""),
                    })
                  }
                  className={formErrors.ppeCode ? "error" : ""}
                  placeholder="e.g., HELMET_001"
                  maxLength={50}
                />
                {formErrors.ppeCode && (
                  <span className="field-error">{formErrors.ppeCode}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="ppeName">
                  PPE Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="ppeName"
                  value={formData.ppeName}
                  onChange={(e) =>
                    setFormData({ ...formData, ppeName: e.target.value })
                  }
                  className={formErrors.ppeName ? "error" : ""}
                  placeholder="e.g., Safety Helmet"
                  maxLength={100}
                />
                {formErrors.ppeName && (
                  <span className="field-error">{formErrors.ppeName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  <option value="">Select Category</option>
                  <option value="Head Protection">Head Protection</option>
                  <option value="Eye Protection">Eye Protection</option>
                  <option value="Face Protection">Face Protection</option>
                  <option value="Hearing Protection">Hearing Protection</option>
                  <option value="Hand Protection">Hand Protection</option>
                  <option value="Foot Protection">Foot Protection</option>
                  <option value="Respiratory Protection">
                    Respiratory Protection
                  </option>
                  <option value="Body Protection">Body Protection</option>
                  <option value="Fall Protection">Fall Protection</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter description (optional)"
                  rows={4}
                  maxLength={255}
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.ppeId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.ppeName}</strong>?
          </p>
          <p className="warning-text">
            This item will be marked as inactive. This action cannot be
            undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default PPEList;
